/* eslint-disable react/no-array-index-key, consistent-return, @typescript-eslint/no-shadow, prettier/prettier, @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { useStyletron } from 'baseui';

interface FlowChartProps {
	diagram: string;
}

let mermaidLoaded: any = null;
const loadMermaid = (): Promise<any> => {
	if (mermaidLoaded) return Promise.resolve(mermaidLoaded);
	return new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = 'https://cdn.jsdelivr.net/npm/mermaid@9.4.3/dist/mermaid.min.js';
		script.onload = () => {
			const m = (window as any).mermaid;
			m.initialize({ startOnLoad: false, theme: 'default', securityLevel: 'loose' });
			mermaidLoaded = m;
			resolve(m);
		};
		script.onerror = reject;
		document.head.appendChild(script);
	});
};

const sanitizeMermaidDiagram = (input: string): string => {
	if (!input) return input;
	return input
		.replace(/→/g, '->')
		.replace(/←/g, '<-')
		.replace(/↔/g, '<->')
		.replace(/⇒/g, '=>')
		.replace(/⇐/g, '<=')
		.replace(/—/g, '-')
		.replace(/–/g, '-')
		.replace(/\u00A0/g, ' ');
};

const FlowChart: React.FC<FlowChartProps> = ({ diagram }) => {
	const [css] = useStyletron();
	const containerRef = useRef<HTMLDivElement>(null);
	const [svgContent, setSvgContent] = useState<string>('');
	const [error, setError] = useState<string>('');

	useEffect(() => {
		if (!diagram) return;

		let cancelled = false;
		const cleanDiagram = sanitizeMermaidDiagram(diagram);

		loadMermaid()
			.then((m) => {
				const id = `mermaid-${Date.now()}`;
				try {
					m.render(id, cleanDiagram, (svg: string) => {
						if (!cancelled) {
							setSvgContent(svg);
							setError('');
						}
					});
				} catch (err: any) {
					if (!cancelled) {
						setError(err?.message || 'Failed to render diagram');
						setSvgContent('');
					}
				}
			})
			.catch((err: any) => {
				if (!cancelled) {
					setError(err?.message || 'Failed to render diagram');
					setSvgContent('');
				}
			});

		return () => {
			cancelled = true;
		};
	}, [diagram]);

	if (error) {
		return (
			<div>
				<div
					className={css({
						padding: '16px',
						backgroundColor: '#FFF3F0',
						borderRadius: '8px',
						color: '#E11900',
						fontSize: '14px',
						marginBottom: '8px'
					})}>
					Failed to render flow diagram: {error}
				</div>
				<pre
					className={css({
						padding: '12px',
						backgroundColor: '#FAFAFA',
						borderRadius: '8px',
						border: '1px solid #E2E2E2',
						fontSize: '12px',
						overflow: 'auto',
						whiteSpace: 'pre-wrap',
						maxHeight: '400px'
					})}>
					{diagram}
				</pre>
			</div>
		);
	}

	if (!svgContent) {
		return <div className={css({ padding: '16px', color: '#666' })}>Loading diagram...</div>;
	}

	return (
		<div
			ref={containerRef}
			className={css({
				overflow: 'auto',
				maxHeight: '500px',
				padding: '16px',
				backgroundColor: '#FAFAFA',
				borderRadius: '8px',
				border: '1px solid #E2E2E2'
			})}
			dangerouslySetInnerHTML={{ __html: svgContent }}
		/>
	);
};

export default FlowChart;
